import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { List, X } from "@phosphor-icons/react";
import logo from "assets/logo.png";

const Header = () => {
  const { isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Close drawer when screen size becomes large
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;

    if (isDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalStyle;
    }

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [isDrawerOpen]);

  const getInitials = (u) => {
    const name = u?.name || u?.fullName || u?.email || "";
    const parts = String(name).trim().split(/\s+|@/);
    const first = parts[0]?.[0] || "";
    const second = parts[1]?.[0] || "";
    return (first + second).toUpperCase() || "U";
  };

  const navLinkClass = ({ isActive }) =>
    `px-2 py-1 rounded transition-colors ${
      isActive ? "text-[#003F75]" : "text-gray-600 hover:text-gray-800"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-gray-100">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center gap-3">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <img src={logo} alt="logo" className="w-28 object-contain" />
          </Link>

          {/* Desktop Nav */}
          {isLoggedIn && (
            <nav className="ml-2 hidden lg:flex items-center gap-3 text-sm">
              <NavLink to="/" end className={navLinkClass}>
                Home
              </NavLink>
              <NavLink to="/tracking" className={navLinkClass}>
                Tracking
              </NavLink>
              <NavLink to="/announcements" className={navLinkClass}>
                Announcements
              </NavLink>
              <NavLink to="/contact" className={navLinkClass}>
                Contact
              </NavLink>
            </nav>
          )}

          <div className="ml-auto flex items-center gap-2">
            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Open menu"
            >
              <List size={24} weight="bold" />
            </button>

            {/* Desktop Profile/Login Button */}
            {isLoggedIn ? (
              <button
                onClick={() => navigate("/profile")}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
              >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#003F75] text-white text-xs">
                  {getInitials(user)}
                </span>
                <span className="text-xs text-gray-700 max-w-[12rem] truncate">
                  {user?.name || user?.email || "Account"}
                </span>
              </button>
            ) : (
              <Link
                to="/login"
                className="hidden sm:flex px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              >
                Log in
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Drawer - Overlay */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 transition-opacity duration-300"
          onClick={() => setIsDrawerOpen(false)}
        ></div>
      )}

      {/* Mobile Drawer - Content */}
      {isDrawerOpen && (
        <div className="fixed top-0 right-0 bottom-0 w-72 max-w-[80vw] bg-white shadow-xl z-50 transform animate-slide-in-right flex flex-col overflow-hidden h-[100dvh]">
          {/* Drawer Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <img src={logo} alt="logo" className="w-24 object-contain" />
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
            >
              <X size={20} weight="bold" />
            </button>
          </div>

          {/* Drawer Content */}
          <div className="flex-1 overflow-y-auto">
            <nav className="flex flex-col p-4">
              <NavLink
                to="/"
                end
                onClick={() => setIsDrawerOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md mb-1 font-medium ${
                    isActive
                      ? "bg-[#003F75] text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`
                }
              >
                Home
              </NavLink>

              {isLoggedIn && (
                <>
                  <NavLink
                    to="/tracking"
                    onClick={() => setIsDrawerOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-2.5 rounded-md mb-1 font-medium ${
                        isActive
                          ? "bg-[#003F75] text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    Tracking
                  </NavLink>
                  <NavLink
                    to="/announcements"
                    onClick={() => setIsDrawerOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-2.5 rounded-md mb-1 font-medium ${
                        isActive
                          ? "bg-[#003F75] text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    Announcements
                  </NavLink>
                  <NavLink
                    to="/contact"
                    onClick={() => setIsDrawerOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-2.5 rounded-md mb-1 font-medium ${
                        isActive
                          ? "bg-[#003F75] text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    Contact
                  </NavLink>
                </>
              )}
            </nav>
          </div>

          {/* Drawer Footer */}
          <div className="p-4 border-t">
            {isLoggedIn ? (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => {
                    navigate("/profile");
                    setIsDrawerOpen(false);
                  }}
                  className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors w-full"
                >
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#003F75] text-white text-sm">
                    {getInitials(user)}
                  </span>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">
                      {user?.name || "User"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email || ""}
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    logout();
                    setIsDrawerOpen(false);
                  }}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Log out
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsDrawerOpen(false)}
                className="block w-full px-3 py-2 text-center rounded-md bg-[#003F75] text-white hover:bg-[#00376a] transition-colors"
              >
                Log in
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
